<template>
    <div class="signin-page" v-loading="loading">
        <div v-if="loadError && !loading" class="error-state" role="alert">
            <el-alert title="签到信息加载失败" :description="loadError" type="error" :closable="false" show-icon></el-alert>
            <el-button type="primary" plain @click="loadSignData">重新加载</el-button>
        </div>

        <template v-else-if="!loading">
            <SigninProgress
                :avatar-url="avatarUrl"
                :member-name="memberName"
                :sign-days-series="signDaysSeries"
                :has-sign="hasSign"
                :sign-state="signState"
                :signing="signing"
                :cycle="cycle"
                :day-items="dayItems"
                :reward-prompt="rewardPrompt"
                :sign-point="signPoint"
                :sign-growth="signGrowth"
                :images="signinImages"
                @sign-in="handleSignin"
            />
            <SigninRules :rules="rules" :cycle="cycle" :images="signinImages" />
        </template>

        <el-dialog title="签到成功" :visible.sync="successDialogVisible" width="420px" append-to-body>
            <div class="success-content" role="status" aria-live="polite">
                <img class="success-illustration" :src="signinImages.success" width="164" height="120" alt="" aria-hidden="true" />
                <strong>今日签到完成</strong>
                <p v-if="successRewardText">恭喜获得 {{ successRewardText }}</p>
                <p v-else>签到奖励已发放，请明日继续。</p>
            </div>
            <span slot="footer">
                <el-button type="primary" @click="successDialogVisible = false">知道了</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { mapGetters } from "vuex"
import { memberInfo as fetchMemberInfo } from "@/api/member/index"
import { getIsSign, getSignAward, getSigninAccountSum, getSignStatus, submitSignin } from "@/api/member/signin"
import SigninProgress from "./components/signin/SigninProgress.vue"
import SigninRules from "./components/signin/SigninRules.vue"

function toNumber(value) {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

export default {
    name: "signin",
    components: {
        SigninProgress,
        SigninRules
    },
    data() {
        return {
            loading: true,
            signing: false,
            loadError: "",
            signState: 0,
            hasSign: false,
            signDaysSeries: 0,
            cycle: 0,
            rules: [],
            signPoint: 0,
            signGrowth: 0,
            memberDetail: {},
            successTip: {},
            successDialogVisible: false
        }
    },
    computed: {
        ...mapGetters(["member", "defaultHeadImage"]),
        memberName() {
            return this.memberDetail.nickname || (this.member && this.member.nickname) || "尊敬的客户"
        },
        avatarUrl() {
            const headimg = this.memberDetail.headimg || (this.member && this.member.headimg) || this.defaultHeadImage
            return this.$img(headimg)
        },
        signinImages() {
            const basePath = "upload/uniapp/signin/"
            return {
                hero: this.$img(`${basePath}bg4.png`),
                signed: this.$img(`${basePath}signed.png`),
                today: this.$img(`${basePath}sginin.png`),
                award: this.$img(`${basePath}award.png`),
                pending: this.$img(`${basePath}gold1.png`),
                pointCard: this.$img(`${basePath}bg2.png`),
                growthCard: this.$img(`${basePath}bg3.png`),
                ruleLeft: this.$img(`${basePath}icon2.png`),
                ruleRight: this.$img(`${basePath}icon1.png`),
                success: this.$img(`${basePath}bg1.png`)
            }
        },
        rewardPrompt() {
            const rewardParts = []
            if (this.nextReward.point) rewardParts.push(`${this.nextReward.point} 积分`)
            if (this.nextReward.growth) rewardParts.push(`${this.nextReward.growth} 成长值`)
            const dayLabel = this.hasSign ? "明日" : "今日"
            return rewardParts.length ? `${dayLabel}签到可获得 ${rewardParts.join("、")}` : `${dayLabel}签到可获得活动奖励`
        },
        nextReward() {
            const nextDay = this.signDaysSeries + 1
            return this.getRewardForDay(nextDay)
        },
        rewardRuleMap() {
            return this.rules.slice(1).reduce((result, item) => {
                result[toNumber(item.day)] = item
                return result
            }, {})
        },
        dayItems() {
            return Array.from({ length: this.cycle }, (item, index) => {
                const day = index + 1
                const reward = this.getRewardForDay(day)
                return {
                    day,
                    point: reward.point,
                    growth: reward.growth,
                    isBonus: Boolean(this.rewardRuleMap[day]),
                    state: this.getDayState(day)
                }
            })
        },
        successRewardText() {
            const rewardParts = []
            const point = toNumber(this.successTip.point)
            const growth = toNumber(this.successTip.growth)
            if (point) rewardParts.push(`${point} 积分`)
            if (growth) rewardParts.push(`${growth} 成长值`)
            return rewardParts.join("、")
        }
    },
    created() {
        this.loadSignData()
    },
    methods: {
        responseData(response, fallbackMessage) {
            if (!response || response.code != 0) {
                throw new Error((response && response.message) || fallbackMessage)
            }
            return response.data
        },
        async loadSignData() {
            this.loading = true
            this.loadError = ""
            try {
                const [statusResponse, awardResponse, isSignResponse, memberResponse, pointResponse, growthResponse] = await Promise.all([
                    getSignStatus(),
                    getSignAward(),
                    getIsSign(),
                    fetchMemberInfo(),
                    getSigninAccountSum({ account_type: "point", from_type: "signin" }),
                    getSigninAccountSum({ account_type: "growth", from_type: "signin" })
                ])
                const statusData = this.responseData(statusResponse, "无法获取签到活动状态") || {}
                const awardData = this.responseData(awardResponse, "无法获取签到奖励规则") || {}
                const memberData = this.responseData(memberResponse, "无法获取客户签到信息") || {}
                this.signState = toNumber(statusData.is_use)
                this.cycle = Math.max(0, toNumber(awardData.cycle))
                this.rules = Array.isArray(awardData.reward) ? awardData.reward : []
                this.hasSign = Boolean(toNumber(this.responseData(isSignResponse, "无法获取今日签到状态")))
                this.memberDetail = memberData
                this.signDaysSeries = Math.max(0, toNumber(memberData.sign_days_series))
                this.signPoint = toNumber(this.responseData(pointResponse, "无法获取签到累计积分"))
                this.signGrowth = toNumber(this.responseData(growthResponse, "无法获取签到累计成长值"))
            } catch (error) {
                this.loadError = error.message || "网络异常，请稍后重试"
            } finally {
                this.loading = false
            }
        },
        getRewardForDay(day) {
            const dailyReward = this.rules[0] || {}
            const bonusReward = this.rewardRuleMap[day] || {}
            return {
                point: toNumber(dailyReward.point) + toNumber(bonusReward.point),
                growth: toNumber(dailyReward.growth) + toNumber(bonusReward.growth)
            }
        },
        getDayState(day) {
            if (this.hasSign && day === this.signDaysSeries) return "today"
            if (day <= this.signDaysSeries) return "signed"
            if (!this.hasSign && day === this.signDaysSeries + 1) return "next"
            return "pending"
        },
        async handleSignin() {
            if (this.signState === 0 || this.hasSign || this.signing) return
            this.signing = true
            try {
                const response = await submitSignin()
                const rewardData = this.responseData(response, "签到失败，请稍后重试") || {}
                this.successTip = rewardData
                this.hasSign = true
                this.signDaysSeries += 1
                this.successDialogVisible = true
                await this.refreshSigninSummary()
            } catch (error) {
                this.$message.error(error.message || "签到失败，请稍后重试")
            } finally {
                this.signing = false
            }
        },
        async refreshSigninSummary() {
            try {
                const [isSignResponse, memberResponse, pointResponse, growthResponse] = await Promise.all([
                    getIsSign(),
                    fetchMemberInfo(),
                    getSigninAccountSum({ account_type: "point", from_type: "signin" }),
                    getSigninAccountSum({ account_type: "growth", from_type: "signin" })
                ])
                if (isSignResponse.code == 0) this.hasSign = Boolean(toNumber(isSignResponse.data))
                if (memberResponse.code == 0 && memberResponse.data) {
                    this.memberDetail = memberResponse.data
                    this.signDaysSeries = Math.max(0, toNumber(memberResponse.data.sign_days_series))
                }
                if (pointResponse.code == 0) this.signPoint = toNumber(pointResponse.data)
                if (growthResponse.code == 0) this.signGrowth = toNumber(growthResponse.data)
            } catch (error) {
                this.$message.warning("签到成功，最新累计数据稍后刷新")
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.signin-page {
    position: relative;
    min-height: 640px;
}

.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 420px;
    padding: 32px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    background: #ffffff;
}

.error-state .el-alert {
    width: 100%;
    max-width: 560px;
    margin-bottom: 20px;
}

.success-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 0 4px;
    text-align: center;
}

.success-illustration {
    width: 164px;
    height: 120px;
    margin-bottom: 12px;
    object-fit: contain;
}

.success-content strong {
    color: #303133;
    font-size: 20px;
}

.success-content p {
    margin: 8px 0 0;
    color: #606266;
}
</style>
